import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DateRangePicker,
  type DateRangeValue,
} from "@/components/ui/date-range-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDateForAPI } from "@/lib/date-utils";
import { formatDate } from "@/lib/utils";
import {
  AGREEMENT_FILTER_OPTIONS,
  ALL_AGREEMENTS,
  agreementFilterLabel,
  type AgreementFilterValue,
} from "@/services/admin-agreement.service";
import {
  USER_STATUS_OPTIONS,
  useUserFiltersStore,
} from "@/stores/userFiltersStore";
import type { UserStatus } from "@/types/admin";
import { Filter, Loader2, Search, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

const SEARCH_DEBOUNCE_MS = 750;

/** Labels the date badge for a range the picker may have left half-open. */
function formatRegisteredRange(from?: string, to?: string): string {
  if (from && to) {
    return `${formatDate(from)} \u2013 ${formatDate(to)}`;
  }

  if (from) {
    return `From ${formatDate(from)}`;
  }

  return to ? `Until ${formatDate(to)}` : "";
}

interface UserFiltersProps {
  /**
   * Rendered immediately after the search box, before the active-filter chips.
   *
   * The slot exists because this component owns the left half of the toolbar while the page owns
   * the right half, and the shared list-screen order puts the screen's own action (here: Export
   * Excel) next to the search input rather than at the far end of the icons.
   */
  actions?: ReactNode;
}

export function UserFilters({ actions }: UserFiltersProps) {
  const filters = useUserFiltersStore((state) => state.filters);
  const setFilter = useUserFiltersStore((state) => state.setFilter);
  const patchFilters = useUserFiltersStore((state) => state.patchFilters);
  const resetCount = useUserFiltersStore((state) => state.resetCount);
  const [search, setSearchInput] = useState(filters.search || "");

  // Tracks the last value this component pushed into the store
  const lastPushedSearch = useRef(filters.search || "");

  useEffect(() => {
    const storeSearch = filters.search || "";

    if (storeSearch !== lastPushedSearch.current) {
      lastPushedSearch.current = storeSearch;
      setSearchInput(storeSearch);
    }
  }, [filters.search]);

  // The effect above cannot see a reset that lands while a term is still being typed: the box has
  // not written to the store yet, so `filters.search` was already "" and stays "" — the dependency
  // never changes and nothing clears. The box then kept the abandoned term and, 750ms later, its
  // debounce pushed it back, so the list filtered by it again and the reset looked like it had
  // simply not happened. `resetCount` is the signal that says "the operator asked to start over",
  // which the filter values alone cannot carry.
  //
  // Clearing `search` also cancels the pending push: the debounce effect below re-runs on the new
  // value and its cleanup drops the timer that was about to write the old one.
  //
  // The first run is skipped, and that is not a detail: an effect keyed on a counter also fires on
  // mount, where clearing would wipe a term restored from sessionStorage off the screen while the
  // store still held it — and the debounce would then push the empty box back and destroy the
  // saved filter. Only a CHANGE in the count is a reset.
  const seenResetCount = useRef(resetCount);
  useEffect(() => {
    if (seenResetCount.current === resetCount) {
      return;
    }

    seenResetCount.current = resetCount;
    setSearchInput("");
    lastPushedSearch.current = "";
  }, [resetCount]);

  useEffect(() => {
    const trimmed = search.trim();

    if (trimmed === (filters.search || "")) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      lastPushedSearch.current = trimmed;
      setFilter("search", trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [filters.search, search, setFilter]);

  const dateRangeValue: DateRangeValue = {
    from: filters.registeredFrom,
    to: filters.registeredTo,
  };

  const handleDateRangeChange = (value: DateRangeValue) => {
    // Both ends go to the server as the picked calendar day plus this client's UTC offset
    // (2026-08-21T00:00:00+07:00), and the server expands each to the whole of that day. Sending
    // raw picker output instead made the "to" end an upper bound at 00:00, which hid everyone
    // registered later that same day — picking 20/08–21/08 dropped every user created on 21/08.
    // One write, not two: each setter returns the list to page 1 and starts a request, so
    // pushing the ends separately fired two queries for one interaction.
    patchFilters({
      registeredFrom: formatDateForAPI(value.from),
      registeredTo: formatDateForAPI(value.to),
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-center">
      {/* Search Input */}
      <div className="w-full sm:w-[360px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          {/* The debounce window has no isFetching to report, so the box says so itself: a term
              typed but not yet committed leaves the table showing the previous result, and without
              this the screen sits motionless for three quarters of a second after every keystroke
              and reads as frozen. Note the local box state here is `search`, the committed value is
              `filters.search` — the reverse of the other list screens. */}
          {search !== (filters.search || "") && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
          <Input
            placeholder="Search user ID, name, email, phone"
            value={search}
            onChange={(e) => setSearchInput(e.target.value)}
            // React withholds onChange while an IME composition is open — which is the whole
            // time a Vietnamese Telex/VNI method is assembling a word — so the term only
            // reached state once a space closed the composition, and the search appeared to
            // need a trailing space to run at all.
            onCompositionEnd={(e) => setSearchInput(e.currentTarget.value)}
            className="pl-10 pr-9"
          />
        </div>
      </div>

      {actions}

      {/* Active Filter Badges */}
      <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:justify-end">
        {filters.search && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            Search: {filters.search}
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setFilter("search", "");
              }}
              aria-label="Clear search filter"
              className="rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {filters.status !== "All" && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            Status: {filters.status}
            <button
              type="button"
              onClick={() => setFilter("status", "All")}
              aria-label="Clear status filter"
              className="rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {(filters.registeredFrom || filters.registeredTo) && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            {/* Either end alone is still a live filter — the picker emits the first click as
                from-only — so the badge (and its clear button) must render for a half-open range
                too, or the filter applies with no visible way to undo it. */}
            Created:{" "}
            {formatRegisteredRange(
              filters.registeredFrom,
              filters.registeredTo,
            )}
            <button
              type="button"
              onClick={() =>
                patchFilters({
                  registeredFrom: undefined,
                  registeredTo: undefined,
                })
              }
              aria-label="Clear date filter"
              className="rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {filters.agreement !== ALL_AGREEMENTS && (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
          >
            Commitment: {agreementFilterLabel(filters.agreement)}
            <button
              type="button"
              onClick={() => setFilter("agreement", ALL_AGREEMENTS)}
              aria-label="Clear commitment filter"
              className="rounded-sm hover:text-destructive focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
      </div>

      {/* Filters Popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            title="Filters"
            aria-label="Filters"
            className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="userStatusFilter">Status</Label>
            <Select
              value={filters.status || "All"}
              onValueChange={(value) =>
                setFilter("status", value as UserStatus | "All")
              }
            >
              <SelectTrigger id="userStatusFilter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {USER_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="userCreatedDateFilter">Created Date</Label>
            <DateRangePicker
              value={dateRangeValue}
              onChange={handleDateRangeChange}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="userAgreementFilter">Commitment</Label>
            <Select
              value={filters.agreement}
              onValueChange={(value) =>
                setFilter("agreement", value as AgreementFilterValue)
              }
            >
              <SelectTrigger id="userAgreementFilter">
                <SelectValue placeholder="All Commitments" />
              </SelectTrigger>
              <SelectContent>
                {AGREEMENT_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
